import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reportConversion } from '../utils/gtag';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function Hero({
  backgroundImage = '/images/carservice2.png', // Back-compat single image
  backgroundImages, // Optional array of images for slider
  overlay = true,
  autoRotateInterval = 6000,
  showArrows = false,
}) {
  const { t } = useTranslation();
  const whatsappBase = 'https://wa.me/971524333690';
  const mkWhatsApp = (text) => `${whatsappBase}?text=${encodeURIComponent(text)}`;

  // Build slides list (fallback to single image)
  const slides = useMemo(() => {
    const arr = Array.isArray(backgroundImages) && backgroundImages.length > 0
      ? backgroundImages
      : [backgroundImage];
    return arr;
  }, [backgroundImages, backgroundImage]);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useReducedMotion?.() || false;
  const timerRef = useRef(null);

  const offers = useMemo(() => [
    {
      title: t('offers.diagnostics'),
      whatsappText: "Hi Vigor Auto, I'd like to book Free Computer Diagnostics.",
      gradient: 'from-emerald-500/30 via-teal-500/25 to-cyan-500/30',
      borderColor: 'border-emerald-300/60',
      hoverBorder: 'hover:border-emerald-200',
      hoverShadow: 'hover:shadow-[0_14px_45px_rgba(16,185,129,0.35)]',
      ring: 'focus-visible:ring-emerald-400',
      overlayGradient: 'from-emerald-400/20',
      iconAura: 'bg-emerald-400/30',
      iconWrapper: 'bg-gradient-to-br from-emerald-500/40 via-teal-400/25 to-cyan-500/35 shadow-[0_18px_40px_rgba(16,185,129,0.4)]',
      iconInner: 'bg-gradient-to-br from-emerald-500/30 via-teal-400/20 to-cyan-500/25',
      iconSvg: (
        <svg viewBox="0 0 32 32" className="h-8 w-8 text-white" fill="none">
          <rect x="5.25" y="6" width="21.5" height="14.5" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8 15h4l1.5-3.2 2.7 6.6 1.9-4.1 5.6.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9.5 21.25h13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M13 21.5v3.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M19 21.5v3.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      title: t('offers.oilChange'),
      whatsappText: "Hi Vigor Auto, I'd like to book Oil Change + Free Items.",
      gradient: 'from-amber-500/30 via-orange-500/25 to-red-500/30',
      borderColor: 'border-amber-300/60',
      hoverBorder: 'hover:border-amber-200',
      hoverShadow: 'hover:shadow-[0_14px_45px_rgba(251,191,36,0.35)]',
      ring: 'focus-visible:ring-amber-400',
      overlayGradient: 'from-amber-400/20',
      iconAura: 'bg-amber-400/30',
      iconWrapper: 'bg-gradient-to-br from-amber-500/35 via-orange-500/30 to-red-500/35 shadow-[0_18px_40px_rgba(251,191,36,0.4)]',
      iconInner: 'bg-gradient-to-br from-amber-400/25 via-orange-400/20 to-red-500/25',
      iconSvg: (
        <svg viewBox="0 0 32 32" className="h-8 w-8 text-white" fill="none">
          <path d="M16.2 8l6.3 6.8c.9.9 1.4 2.1 1.4 3.3 0 2.9-2.3 5.2-5.2 5.2s-5.2-2.3-5.2-5.2c0-1.1.3-2.1.9-3l1.8-2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 23.5h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M9 11.4l6-5 2.4 2.4-6 5c-.5.4-1.2.4-1.6 0l-.8-.9c-.5-.5-.4-1.3 0-1.7z" fill="currentColor" />
          <path d="M20.5 18.5c0 1.2-1 2.2-2.2 2.2s-2.2-1-2.2-2.2.8-2.3 2.2-3.7c1.4 1.4 2.2 2.5 2.2 3.7z" fill="currentColor" />
        </svg>
      ),
    },
    {
      title: t('offers.pickup'),
      whatsappText: "Hi Vigor Auto, I'd like Free Pickup & Dropoff and Free Inspection.",
      gradient: 'from-sky-500/30 via-blue-500/25 to-violet-500/30',
      borderColor: 'border-sky-300/60',
      hoverBorder: 'hover:border-sky-200',
      hoverShadow: 'hover:shadow-[0_14px_45px_rgba(56,189,248,0.35)]',
      ring: 'focus-visible:ring-sky-400',
      overlayGradient: 'from-sky-400/20',
      iconAura: 'bg-sky-400/30',
      iconWrapper: 'bg-gradient-to-br from-sky-500/35 via-blue-500/25 to-violet-500/35 shadow-[0_18px_40px_rgba(56,189,248,0.4)]',
      iconInner: 'bg-gradient-to-br from-sky-400/25 via-blue-400/20 to-indigo-500/25',
      iconSvg: (
        <svg viewBox="0 0 32 32" className="h-8 w-8 text-white" fill="none">
          <path d="M6.5 18.5h19l-1.2-4c-.3-.9-1.1-1.5-2.1-1.5h-12c-1 0-1.8.6-2.1 1.5l-1.6 4z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9.5 18.5a2.7 2.7 0 11-5.4 0 2.7 2.7 0 015.4 0z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M28.1 18.5a2.7 2.7 0 11-5.4 0 2.7 2.7 0 015.4 0z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M4.5 13.5l1.2-4.1c.2-.8 1-1.4 1.8-1.4h12.2c.8 0 1.6.5 1.8 1.3l1.1 4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6.5 24.5l3-2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M25.5 22.3l3 2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      title: t('offers.brands'),
      whatsappText: "Hi Vigor Auto, I'd like to service my car — All Major Brands.",
      gradient: 'from-pink-500/30 via-rose-500/25 to-indigo-500/30',
      borderColor: 'border-pink-300/60',
      hoverBorder: 'hover:border-pink-200',
      hoverShadow: 'hover:shadow-[0_14px_45px_rgba(236,72,153,0.35)]',
      ring: 'focus-visible:ring-pink-400',
      overlayGradient: 'from-pink-400/20',
      iconAura: 'bg-pink-400/30',
      iconWrapper: 'bg-gradient-to-br from-pink-500/35 via-rose-500/25 to-indigo-500/35 shadow-[0_18px_40px_rgba(236,72,153,0.4)]',
      iconInner: 'bg-gradient-to-br from-pink-400/25 via-rose-400/20 to-indigo-500/25',
      iconSvg: (
        <svg viewBox="0 0 32 32" className="h-8 w-8 text-white" fill="none">
          <path d="M16 5.8l9 4.4v6.9c0 6-3.8 9.7-9 11.1-5.2-1.4-9-5.1-9-11.1V10.2L16 5.8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M16 14.5l2.2 3.3H13.8L16 14.5z" fill="currentColor" />
          <path d="M16 20.8l-2.6-3.8h5.2L16 20.8z" fill="currentColor" />
          <path d="M11.8 13.8l4.2-6.5 4.2 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    }
  ], [t]);

  const next = useCallback(() => setIndex((i) => (i + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + slides.length) % slides.length), [slides.length]);

  // Auto-rotate
  useEffect(() => {
    if (slides.length <= 1) return;
    if (prefersReducedMotion) return; // honor reduced motion
    if (paused) return;
    timerRef.current && clearInterval(timerRef.current);
    timerRef.current = setInterval(next, autoRotateInterval);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [slides.length, paused, autoRotateInterval, prefersReducedMotion, next]);

  // Animations
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { when: 'beforeChildren', staggerChildren: 0.12 }
    }
  };
  const fadeUp = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };
  const cardVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 14, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };
  const iconPop = {
    hidden: { opacity: 0, scale: prefersReducedMotion ? 1 : 0.9, y: prefersReducedMotion ? 0 : 6 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.05 }
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 text-white overflow-hidden" aria-label="Hero primary introduction">
      {/* Background slider (crossfade + subtle zoom) */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        {slides.map((src, i) => (
          <motion.div
            key={src + i}
            className="absolute inset-0"
            aria-hidden="true"
            initial={{ opacity: i === index ? 1 : 0, scale: prefersReducedMotion ? 1 : 1.08 }}
            animate={{ opacity: i === index ? 1 : 0, scale: prefersReducedMotion ? 1 : i === index ? 1 : 1.08 }}
            transition={{ duration: prefersReducedMotion ? 0 : 1.2, ease: [0.22, 1, 0.36, 1] }}
            style={{
              backgroundImage: `url(${src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'brightness(1.1) contrast(1.15) saturate(1.3)',
            }}
          />
        ))}
      </div>
      {/* Rich gradient overlay for better text contrast */}
      {overlay && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/85 via-blue-900/70 to-indigo-900/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-blue-950/90 via-transparent to-transparent" />
        </>
      )}
      {/* Multiple accent lights for richness */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(245,211,19,0.15),transparent_50%)] mix-blend-overlay" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(59,130,246,0.2),transparent_60%)] mix-blend-overlay" />
      <motion.div
        className="relative max-w-7xl mx-auto px-4 pt-6 pb-28 sm:pt-28 text-center"
        initial="hidden"
        animate="visible"
        variants={container}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Slider controls (show only when multiple slides) */}
        {slides.length > 1 && showArrows && (
          <div className="pointer-events-auto absolute left-0 right-0 top-3 sm:top-6 flex items-center justify-between px-3 sm:px-6 select-none">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous background image"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm border border-white/20 shadow transition"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next background image"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm border border-white/20 shadow transition"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
            </button>
          </div>
        )}
        {/* New concise context badge with enhanced styling */}
        <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/20 backdrop-blur-xl border border-white/30 text-xs font-bold tracking-wide uppercase text-white shadow-lg shadow-black/20 mb-5">
          <span className="h-2 w-2 rounded-full bg-[#F5D313] animate-pulse shadow-[0_0_8px_rgba(245,211,19,0.8)]" />
          <span className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">Your Dealer-Alternative Workshop in Dubai</span>
        </motion.div>
        <motion.h1 variants={fadeUp} className="text-3xl md:text-5xl font-extrabold tracking-tight mb-8 drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)] text-white">
          <span className="block">{t('hero.title.main')}</span>
          <span className="mt-2 block text-2xl md:text-3xl font-semibold text-white/95">{t('hero.title.sub')}</span>
        </motion.h1>
        <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-2 mb-10">
          
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm font-semibold text-white shadow-lg">
            <span className="text-base">🇯🇵</span>
            Japanese
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm font-semibold text-white shadow-lg">
            <span className="text-base">🇰🇷</span>
            Korean
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm font-semibold text-white shadow-lg">
            <span className="text-base">🇪🇺</span>
            European
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm font-semibold text-white shadow-lg">
            <span className="text-base">🇺🇸</span>
            American
          </span>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm font-semibold text-white shadow-lg">
            <span className="text-base">🇨🇳</span>
            Chinese
          </span>
        
        </motion.div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          
        </div>
  {/* Optional trust blurb */}
        <motion.div variants={container} className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 text-sm">
          {offers.map((offer) => (
            <motion.a
              key={offer.title}
              variants={cardVariants}
              whileHover={prefersReducedMotion ? undefined : { y: -4, scale: 1.04 }}
              whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
              href={mkWhatsApp(offer.whatsappText)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reportConversion(mkWhatsApp(offer.whatsappText))}
              className={`group relative block bg-gradient-to-br ${offer.gradient} backdrop-blur-xl border-2 ${offer.borderColor} rounded-2xl p-5 text-center ${offer.hoverBorder} ${offer.hoverShadow} transition-all duration-300 focus:outline-none focus-visible:ring-2 ${offer.ring} overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.3)]`}
              aria-label={`Chat on WhatsApp about ${offer.title}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${offer.overlayGradient} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
              <div className="relative w-fit mx-auto mb-4">
                <span className={`pointer-events-none absolute -inset-4 rounded-[28px] blur-2xl opacity-60 transition-all duration-500 group-hover:opacity-85 ${offer.iconAura}`} />
                <motion.div
                  variants={iconPop}
                  className={`relative h-16 w-16 flex items-center justify-center rounded-[22px] overflow-hidden border border-white/15 backdrop-blur-lg transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[1deg] ${offer.iconWrapper}`}
                >
                  <span className={`absolute inset-[2px] rounded-[20px] opacity-90 ${offer.iconInner}`} />
                  <span className="absolute inset-0 rounded-[22px] bg-white/8 mix-blend-overlay" />
                  <span className="relative z-[1] text-white">{offer.iconSvg}</span>
                </motion.div>
              </div>
              <span className="relative font-bold text-white text-base drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">{offer.title}</span>
            </motion.a>
          ))}
        </motion.div>

        {/* Dots */}
        {slides.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-2.5 w-2.5 rounded-full transition border ${i === index ? 'bg-white border-white' : 'bg-white/30 border-white/40 hover:bg-white/60'}`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </section>
  );
}
